import { createAsyncThunk } from '@reduxjs/toolkit'

import {
  fetchHotels,
  createHotel,
  deleteHotel,
  updateHotel,
  uploadHotelImage,
  deleteHotelImage,
  uploadHotelVideo,
  deleteHotelVideo,
} from '../../services/hotels'

import { toast } from 'react-toastify';

export const getHotels = createAsyncThunk(
  'hotels/fetchHotels',
  async (thunkAPI) => {
    try {
      const res = await fetchHotels()
      return res.data
    } catch (err) {
      return thunkAPI.rejectWithValue({ error: err.message })
    }
  },
)

/**
 * Process image uploads for a hotel.
 * - mainImageFile: File | null  → uploads to slot 'main'
 * - galleryFiles: Array<File | string | null> → index-based slots
 *   - File → upload new image to gallery_{i}
 *   - string → existing URL, no change
 *   - null → slot was cleared, delete from storage
 * - previousGalleryUrls: string[] → old URLs to know which slots existed before
 */
async function processHotelImages(hotelId, { mainImageFile, galleryFiles, previousGalleryUrls = [] }) {
  const updates = {}

  // Main image
  if (mainImageFile instanceof File) {
    const url = await uploadHotelImage(hotelId, mainImageFile, 'main')
    if (url) updates.main_image_url = url
  }

  // Gallery images
  if (Array.isArray(galleryFiles)) {
    const finalUrls = []

    for (let i = 0; i < 5; i++) {
      const item = galleryFiles[i]

      if (item instanceof File) {
        // New file → upload, overwriting any existing at that slot
        const url = await uploadHotelImage(hotelId, item, `gallery_${i}`)
        if (url) finalUrls.push(url)
      } else if (typeof item === 'string' && item.length > 0) {
        // Existing URL → keep it
        finalUrls.push(item)
      } else {
        // null/undefined → slot was removed
        // If there was a previous image at this slot, delete it from storage
        if (previousGalleryUrls[i]) {
          await deleteHotelImage(hotelId, `gallery_${i}`)
        }
      }
    }

    updates.gallery_urls = finalUrls
  }

  return updates
}

/**
 * Process video uploads for a hotel.
 * - videoFiles: Array<File | string | null> → index-based slots (max 2)
 *   - File → upload new video to video_{i}
 *   - string → existing URL, no change
 *   - null → slot was cleared, delete from storage
 * - previousVideoUrls: string[] → old URLs to know which slots existed before
 */
async function processHotelVideos(hotelId, { videoFiles, previousVideoUrls = [] }) {
  const updates = {}

  if (Array.isArray(videoFiles)) {
    const finalUrls = []

    for (let i = 0; i < 2; i++) {
      const item = videoFiles[i]

      if (item instanceof File) {
        // New file → upload, overwriting any existing at that slot
        const url = await uploadHotelVideo(hotelId, item, `video_${i}`)
        if (url) finalUrls.push(url)
      } else if (typeof item === 'string' && item.length > 0) {
        // Existing URL → keep it
        finalUrls.push(item)
      } else {
        // null/undefined → slot was removed
        // If there was a previous video at this slot, delete it from storage
        if (previousVideoUrls[i]) {
          await deleteHotelVideo(hotelId, `video_${i}`)
        }
      }
    }

    updates.video_urls = finalUrls
  }

  return updates
}


export const addHotel = createAsyncThunk(
  'hotels/addHotel',
  async (data, thunkAPI) => {
    try {
      const { mainImageFile, galleryFiles, previousGalleryUrls, videoFiles, previousVideoUrls, ...hotelData } = data

      const res = await toast.promise(
        createHotel(hotelData).then((response) => {
          if (response.error) throw response.error;
          return response;
        }),
        {
          pending: { render() { return "Guardando hotel..." }, icon: false },
          success: {
            render({ data: responseData }) {
              return `Se registró el hotel 😏`
            },
            icon: "🚀",
          },
          error: {
            render({ data: errData }) {
              return errData?.message ? `Error: ${errData.message}` : `Ocurrió un error`
            }
          }
        }
      );

      const hotel = res.data[0]

      // Upload images & videos if any were provided
      const mediaUpdates = {}

      if (mainImageFile || (galleryFiles && galleryFiles.some(f => f))) {
        const imageUpdates = await processHotelImages(hotel.id, { mainImageFile, galleryFiles })
        Object.assign(mediaUpdates, imageUpdates)
      }

      if (videoFiles && videoFiles.some(f => f)) {
        const videoUpdates = await processHotelVideos(hotel.id, { videoFiles })
        Object.assign(mediaUpdates, videoUpdates)
      }

      if (Object.keys(mediaUpdates).length > 0) {
        await updateHotel(hotel.id, mediaUpdates)
        Object.assign(hotel, mediaUpdates)
      }

      return hotel
    } catch (err) {
      return thunkAPI.rejectWithValue({ error: err.message })
    }
  }
)


export const removeHotel = createAsyncThunk(
  'hotels/removeHotel',
  async (id, thunkAPI) => {
    try {
      await toast.promise(
        deleteHotel(id).then((response) => {
          if (response.error) throw response.error;
          return response;
        }),
        {
          pending: { render() { return "Eliminando..." }, icon: false },
          success: {
            render({ data: responseData }) {
              return `Se eliminó el hotel 😏`
            },
            icon: "🚀",
          },
          error: {
            render({ data: errData }) {
              return errData?.message ? `Error: ${errData.message}` : `Ocurrió un error`
            }
          }
        }
      );
      return id
    } catch (err) {
      return thunkAPI.rejectWithValue({ error: err.message })
    }
  }
)

export const editHotel = createAsyncThunk(
  'hotels/editHotel',
  async (data, thunkAPI) => {
    try {
      const { id, mainImageFile, galleryFiles, previousGalleryUrls, videoFiles, previousVideoUrls, ...hotelData } = data

      const res = await toast.promise(
        updateHotel(id, hotelData).then((response) => {
          if (response.error) throw response.error;
          return response;
        }),
        {
          pending: { render() { return "Actualizando hotel..." }, icon: false },
          success: {
            render({ data: responseData }) {
              return `Se actualizó el hotel 😏`
            },
            icon: "🚀",
          },
          error: {
            render({ data: errData }) {
              return errData?.message ? `Error: ${errData.message}` : `Ocurrió un error`
            }
          }
        }
      );

      const hotel = res.data[0]

      // Upload/delete images & videos as needed
      const mediaUpdates = {}

      if (mainImageFile || galleryFiles) {
        const imageUpdates = await processHotelImages(hotel.id, {
          mainImageFile,
          galleryFiles,
          previousGalleryUrls,
        })
        Object.assign(mediaUpdates, imageUpdates)
      }

      if (videoFiles || previousVideoUrls) {
        const videoUpdates = await processHotelVideos(hotel.id, {
          videoFiles,
          previousVideoUrls,
        })
        Object.assign(mediaUpdates, videoUpdates)
      }

      if (Object.keys(mediaUpdates).length > 0) {
        await updateHotel(hotel.id, mediaUpdates)
        Object.assign(hotel, mediaUpdates)
      }

      return hotel
    } catch (err) {
      return thunkAPI.rejectWithValue({ error: err.message })
    }
  }
)
